import { useCallback, useEffect, useMemo, useState } from 'react';
import { Board } from '@/components/Board';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Toolbar } from '@/components/Toolbar';
import type { NoteColor } from '@/enums/noteColor';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useNotesDispatch } from '@/hooks/useNotesDispatch';
import { SessionStore } from '@/services/cache/SessionStore';
import { applyLatencyFromQuery } from '@/services/api/latency';
import { InteractionProvider } from '@/state/InteractionContext';
import { NotesProvider } from '@/state/NotesProvider';
import { redo, undo } from '@/state/notesActions';
import { DEFAULT_NOTE_COLOR } from '@/constants/notes';
import styles from './App.module.css';

function AppShell() {
  const dispatch = useNotesDispatch();
  const sessionStore = useMemo(() => new SessionStore(), []);
  const [selectedColor, setSelectedColor] = useState<NoteColor>(
    () => sessionStore.lastColor ?? DEFAULT_NOTE_COLOR,
  );

  const handleColorSelect = useCallback(
    (color: NoteColor) => {
      setSelectedColor(color);
      sessionStore.setLastColor(color);
    },
    [sessionStore],
  );

  useKeyboardShortcuts({
    onUndo: () => dispatch(undo()),
    onRedo: () => dispatch(redo()),
  });

  return (
    <div className={styles.app}>
      <Toolbar selectedColor={selectedColor} onColorSelect={handleColorSelect} />
      <main className={styles.main}>
        <Board selectedColor={selectedColor} />
      </main>
    </div>
  );
}

export function App() {
  useEffect(() => {
    applyLatencyFromQuery(new URLSearchParams(window.location.search));
  }, []);

  // Close the editor when the user clicks outside the currently-focused note.
  // useDrag calls event.preventDefault() on pointerdown, which suppresses the
  // browser's default focus change — so we blur the active textarea manually
  // in the capture phase (i.e., before useDrag preventDefaults).
  useEffect(() => {
    const handler = (event: PointerEvent) => {
      const active = document.activeElement;
      if (!(active instanceof HTMLTextAreaElement)) return;
      const target = event.target as Node | null;
      if (target && active.contains(target)) return;
      active.blur();
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, []);

  return (
    <ErrorBoundary>
      <InteractionProvider>
        <NotesProvider>
          <AppShell />
        </NotesProvider>
      </InteractionProvider>
    </ErrorBoundary>
  );
}
